import MensagemStore from '@/store/MensagemStore';
import Configuracao from '../misc/Configuracao';
import { IMinutaAssinada, IMinutaAssinadaPage, ITipoMinuta, Minuta } from '../modelo';
import { EmentaDecisao } from '../modelo/EmentaDecisao';
import { HistoricoMinutaDto } from '../modelo/HistoricoMinuta';
import { HistoricoSituacaoMinutaDto } from '../modelo/HistoricoSituacaoMinuta';
import { IMinutaEmEdicaoExterna } from "../modelo/MinutaEmEdicaoExterna";
import { ISituacaoMinuta, SituacaoMinuta } from '../modelo/SituacaoMinuta';
import { ISituacaoPeticao } from '../modelo/SituacaoPeticao';
import { VersaoMinutaDto } from '../modelo/dto/VersaoMinutaDto';
import UsuarioStore from '../store/UsuarioStore';
import { b64EncodeUnicode } from '../utils/Base64Utils';
import MinutaUtils from '../utils/MinutaUtils';
import { apresentarApiErroPadrao } from '@/utils/ApiErroPadraoUtils';
import fetch from './AxiosAdapter';
import { MinutaDto } from './dto/MinutaDto';
import UrlsMinuta from './urls/UrlsMinuta';

interface IResultadoGet {
    identificadorErro: string;
    mensagem: string
}

class MinutaApi {

    config = Configuracao;
    dataToSave64 = '';

    async analisaResult(result, _processo, _minutaHtmlDocx, _tipoMinuta) {
        if (result.status === 400) {
            throw new Error('O processo não se encontra neste Gabinete.');
        } else if (result.status === 409) {
            throw new Error('Já existe minuta em edição.');
        } else if (result.status === 502) {
            console.info(
                'Servidor não encontrado. Salvando minuta localmente.'
            );
        } else if (result.status > 300) {
            result = result.data;
            if (result.mensagem) {
                throw new Error(result.mensagem);
            }
            throw new Error('Não foi possível gravar a minuta. Erro interno.');
        } else {
            return 'Minuta salva.';
        }
    }

    async recuperarMinuta(
        idProcesso,
        idTipoMinuta,
        conteudo: boolean,
        numeroMinuta?: number
    ): Promise<MinutaDto> {
        if (!idTipoMinuta) {
            console.error('tipoMinuta foi passado como undefined!');
            return;
        }

        const result = await fetch<MinutaDto>(
            UrlsMinuta.minutaComConteudo(
                idProcesso,
                idTipoMinuta,
                conteudo,
                numeroMinuta
            ),
            {
                method: 'GET',
                cache: 'no-store',
            }
        );
        return result.data;
    }

    async recuperarMinutaExterna(
        idProcesso,
        numeroMinuta: number,
        conteudo: boolean,
    ): Promise<MinutaDto> {
        const result = await fetch<MinutaDto>(
            UrlsMinuta.minutaExternaComConteudo(
                idProcesso,
                numeroMinuta,
                conteudo
            ),
            {
                method: 'GET',
                cache: 'no-store',
            }
        );
        return result.data;
    }

    public async getTiposMinuta(): Promise<ITipoMinuta[]> {
        const result = await fetch<ITipoMinuta[]>(this.config.urlTiposMinuta, {
            method: 'GET',
        });
        return result.data;
    }

    public async getSituacoesMinuta(): Promise<ISituacaoMinuta[]> {
        const result = await fetch<ISituacaoMinuta[]>(this.config.urlSituacoesMinuta, {
            method: 'GET',
        });
        let situacoes = result.data;
        situacoes = situacoes.sort((a, b) => (a.codigo > b.codigo) ? 1 : -1);
        return situacoes;
    }

    public async getSituacoesPeticao(): Promise<ISituacaoPeticao[]> {
        const result = await fetch<ISituacaoPeticao[]>(this.config.urlSituacoesPeticao, {
            method: 'GET',
        });
        let situacoes = result.data;
        situacoes = situacoes.sort((a, b) => (a.codigo > b.codigo) ? 1 : -1);
        return situacoes;
    }

    public async alterarSituacoesPeticao(idProcesso, idPeticao, situacao) {
        await fetch(this.config.urlAlterarSituacaoPeticao.replace(this.config.idProcesso, idProcesso).replace(this.config.idPeticao, idPeticao), {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: situacao
        });
    }

    public async apreciarPeticao(idProcesso, codIdDocumento) {
        await fetch(this.config.urlApreciarPeticao.replace(this.config.idProcesso, idProcesso).replace(this.config.idPeticao, codIdDocumento), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
    }

    public async desapreciarPeticao(idProcesso, codIdDocumento) {
        await fetch(this.config.urlDesapreciarPeticao.replace(this.config.idProcesso, idProcesso).replace(this.config.idPeticao, codIdDocumento), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
    }

    async getTiposMinutaProcesso(identificadorProcesso) {
        // noinspection UnnecessaryLocalVariableJS
        const resultTiposExistentes = await fetch(
            UrlsMinuta.tiposMinuta(identificadorProcesso),
            {
                method: 'HEAD',
            }
        );
        return resultTiposExistentes;
    }

    async getCategoriasMinuta() {
        const result = await fetch(this.config.urlCategoriasMinuta, {
            method: 'GET',
        });
        return result.data;
    }

    async salvarMinutaDados(
        processo,
        conteudoHTML,
        tipoMinuta,
        login,
        siglaGabinete,
        publicavel,
        categoria,
        labelProcesso,
        tipoDocumentoMinuta
    ) {
        let result = {};
        const categorias = categoria ? [].concat(categoria) : [];
        if (login !== undefined && siglaGabinete !== undefined) {
            const body = {
                tipoDocumentoMinuta: tipoDocumentoMinuta,
                tipo: tipoMinuta,
                siglaOrgaoJulgador: siglaGabinete,
                cpfResponsavel: login,
                publicavel: !!publicavel,
                categoria: categorias,
            };
            try {
                result = await fetch(
                    UrlsMinuta.salvarDados(processo, tipoMinuta.codigo),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        method: 'PUT',
                        cache: 'no-store',
                        body: body,
                    }
                );
            } catch (error) {
                console.error(error);
                console.info(
                    'O servidor não pode ser encontrado. Salvando minuta localmente.'
                );
            }
        }
        try {
            return await this.analisaResult(
                result,
                processo,
                conteudoHTML,
                tipoMinuta
            );
        } catch (erro) {
            throw new Error(erro.message);
        }
    }

    async salvarDadosRevisaoPauta(
        idProcesso: number,
        minuta: Minuta,
        login: string,
        siglaGabinete: string,
        dados: {
            cpfRevisorPauta?: string | null;
            textoRevisaoPauta?: string | null;
            autorizacaoPauta?: boolean;
            statusRevisao?: { numero: number } | null;
        }
    ) {
        if (!idProcesso || !minuta?.tipo?.codigo || !siglaGabinete) {
            return;
        }

        const body = {
            tipoDocumentoMinuta: minuta.tipoDocumentoMinuta || 'docx',
            tipo: minuta.tipo,
            siglaOrgaoJulgador: siglaGabinete,
            cpfResponsavel: login,
            publicavel: !!minuta.publicavel,
            categoria: (minuta.categorias || []),
            cpfRevisorPauta: dados?.cpfRevisorPauta,
            textoRevisaoPauta: dados?.textoRevisaoPauta,
            autorizacaoPauta: dados?.autorizacaoPauta,
            statusRevisao: dados?.statusRevisao,
        };

        await fetch(
            Configuracao.urlMinutaGE + idProcesso + '/' + minuta.tipo.codigo,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'PUT',
                cache: 'no-store',
                body,
            }
        );
    }

    async alterarSituacaoMinutaEmLote(numMinutas: number[], codigoSituacao: number, cpfAlterador: string) {
        await fetch(Configuracao.urlSituacaoMinutaEmLote.replace('{CODIGO_SITUACAO}', codigoSituacao.toString()) + "?cpfAlterador=" + cpfAlterador,
            {
                headers: { 'Content-Type': 'application/json' },
                method: 'PUT',
                cache: 'no-store',
                body: numMinutas,
            }
        );
    }

    async alterarSituacaoMinuta(minuta: Minuta, login, siglaGabinete, processo) {
        let result = {};
        if (login !== undefined && siglaGabinete !== undefined) {
            const body = {
                tipoDocumentoMinuta: minuta.tipoDocumentoMinuta,
                tipo: minuta.tipo,
                siglaOrgaoJulgador: siglaGabinete,
                // cpfAlterador: login,
                situacaoMinuta: minuta.situacaoMinuta
            };
            try {
                result = await fetch(
                    Configuracao.urlSituacaoMinuta.replace("{NUM_MINUTA}", minuta.numero.toString()).replace('{CODIGO_SITUACAO}', minuta.situacaoMinuta.codigo.toString()) + "?cpfAlterador=" + login,
                    {
                        headers: { 'Content-Type': 'application/json' },
                        method: 'PUT',
                        cache: 'no-store',
                        body: body,
                    }
                );
            } catch (error) {
                console.error(error);
                console.info('O servidor não pode ser encontrado. Operação não realizada: alterarSituacaoMinuta.');
            }
        }
        try {
            return await this.analisaResult(
                result,
                processo,
                undefined,
                minuta.tipo
            );
        } catch (erro) {
            throw new Error(erro.message);
        }
    }

    async salvarUnidadePublicacao(
        idProcesso: number,
        tipoMinuta: number,
        unidadePublicacao: number
    ) {
        await fetch(
            UrlsMinuta.salvarUnidadePublicacao(idProcesso, tipoMinuta),
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'PUT',
                cache: 'no-store',
                body: { codigo: unidadePublicacao },
            }
        );
    }

    async salvarUnidadePublicaoLote(
        idProcesso: number,
        unidadePublicacao: number
    ) {
        await fetch(
            UrlsMinuta.salvarUnidadePublicaoLote(idProcesso),
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'PUT',
                cache: 'no-store',
                body: { codigo: unidadePublicacao },
            }
        );
    }

    async salvarMinutaHtmlDocx(
        processo,
        minutaHtmlDocx,
        tipoMinuta,
        login,
        siglaGabinete,
        publicavel,
        categoria,
        labelProcesso,
        tipoDocumentoMinuta = 'docx'
    ) {
        if (this.dataToSave64 === minutaHtmlDocx) {
            return;
        }
        let conteudo;
        if (tipoDocumentoMinuta === 'docx') {
            conteudo = btoa(MinutaUtils.decodeMinuta(minutaHtmlDocx));
        } else {
            // TODO codigo legado do GE1 conferir se pode deletar
            try {
                conteudo = b64EncodeUnicode(
                    MinutaUtils.decodeMinuta(minutaHtmlDocx)
                );
            } catch (e) {
                console.error(
                    'erro de encondig... tentando solução de contorno.',
                    e
                );
                if (e.message.toLowerCase().indexOf('latin1') > -1) {
                    conteudo = MinutaUtils.refazerAssinaturaVazia(
                        processo.relator.nome,
                        '',
                        ''
                    );
                }
            }
        }

        if (login && siglaGabinete && conteudo && conteudo.length > 10) {
            let result = {};
            try {
                result = await fetch(
                    UrlsMinuta.salvarConteudo(processo, tipoMinuta.codigo),
                    {
                        headers: {
                            'Content-Type':
                                tipoDocumentoMinuta &&
                                    tipoDocumentoMinuta === 'docx'
                                    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                    : 'text/html',
                        },
                        method: 'PUT',
                        cache: 'no-store',
                        body: conteudo,
                        silent: true,
                    }
                );
            } catch (error) {
                this.dataToSave64 = '';
                console.warn('salvarMinutaHtmlDocx: endpoint /conteudo indisponível, salvando localmente.', error?.response?.status ?? error?.message);
                console.info(
                    'O servidor não pode ser encontrado. Salvando minuta localmente.'
                );
            }

            try {
                return await this.analisaResult(
                    result,
                    processo,
                    minutaHtmlDocx,
                    tipoMinuta
                );
            } catch (erro) {
                this.dataToSave64 = '';
                throw new Error(erro.message);
            }
        }
    }

    async recuperaMinutas(idProcesso) {
        const result = await fetch(UrlsMinuta.tiposMinuta(idProcesso), {
            method: 'GET',
            cache: 'no-store',
        });
        return result.data;
    }

    async recuperaMinutasArquivadas(idProcesso) {
        const result = await fetch(UrlsMinuta.minutaPorSituacao(idProcesso, SituacaoMinuta.ARQUIVADA), {
            method: 'GET',
            cache: 'no-store',
        });
        return result.data;
    }

    async recuperaMinutasAssinadas(idProcesso): Promise<Minuta[]> {
        const result = await fetch(UrlsMinuta.minutaPorSituacao(idProcesso, SituacaoMinuta.ASSINADA), {
            method: 'GET',
            cache: 'no-store',
        });
        return result.data as Minuta[];
    }

    async arquivarMinutas(numMinutas: number[]) {
        await fetch(Configuracao.urlArquivarMinutas, {
            method: 'POST',
            body: numMinutas
        });
    }

    async gravarFuncaoMagistrado(idProcesso, idTipoMinuta, funcao) {
        const result = await fetch(
            UrlsMinuta.funcaoMagistrado(idProcesso, idTipoMinuta),
            {
                headers: {
                    'Content-Type': 'text/plain',
                },
                method: funcao != undefined && funcao.length > 0 ? 'PUT' : 'DELETE',
                cache: 'no-store',
                body: funcao,
            }
        );
        if (result.status > 300) {
            throw new Error(
                'Não foi possível gravar a função do magistrado na minuta'
            );
        }
        return result.data;
    }

    // async alterarEmEdicaoExterna(
    //     idProcesso: number,
    //     idTipoMinuta: number,
    //     emEdicaoExterna: boolean
    // ) {
    //     const result = await fetch(
    //         UrlsMinuta.alterarEmEdicaoExterna(idProcesso, idTipoMinuta),
    //         {
    //             headers: {
    //                 'Content-Type': 'text/plain',
    //             },
    //             method: 'PUT',
    //             cache: 'no-store',
    //             body: emEdicaoExterna ? 'S' : 'N',
    //         }
    //     );
    //     if (result.status > 300) {
    //         throw new Error(
    //             'Não foi possível alterar o estado de edição externa da minuta'
    //         );
    //     }
    //     return result.data;
    // }

    liberarVotosEmLote = async (idsProcessos: number[]) => {
        return await fetch<IResultadoGet>(
            Configuracao.urlLiberarConteudoVotoEmLote,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: idsProcessos
            });
    }

    cancelarVotosEmLote = async (idsProcessos: number[]) => {
        const result = await fetch<IResultadoGet>(
            Configuracao.urlCancelarLiberacaoConteudoVotoEmLote,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: idsProcessos
            });

        return result.data;
    }

    async consultarLiberacaoConteudoVotoEmLote(idsProcesso: number[]): Promise<boolean[]> {
        const result = await fetch<IResultadoGet>(UrlsMinuta.consultaVotoEmLote(idsProcesso),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: idsProcesso
            });
        if (result && result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error('Não foi possível consultar a situação da liberação do voto.' + mensagem);
        }
        return result.data as any;
    }

    async atualizarPlanilhaEmLote(idsProcesso: number[]) {
        const result = await fetch<IResultadoGet>(
            UrlsMinuta.atualizaPlanilhaEmLote(idsProcesso).replace('{SIGLA_GABINETE}', UsuarioStore.gabinete),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: idsProcesso
            });
        if (result && result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error(
                'Não foi possível atualizar as minutas na planilha.' +
                mensagem
            );
        }
    }

    //MANTEM
    async forcaVistoLiberado(idProcesso: number, gabinete: string) {
        const result = await fetch<IResultadoGet>(
            UrlsMinuta.forcaVistoLiberado(idProcesso, gabinete),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: idProcesso
            });
        if (result && result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error(
                'Não foi possível atualizar para visto assinado.' +
                mensagem
            );
        }
    }

    async consultarLiberacaoConteudoVoto(idProcesso: number): Promise<boolean> {
        const result = await fetch<IResultadoGet>(
            Configuracao.urlConsultaLiberacaoVoto.replace('{ID_PROCESSO}', idProcesso.toString()),
            {
                method: 'GET',
                cache: 'no-store'
            }
        );
        if (result && result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error(
                'Não foi possível consultar a situação da liberação do voto.' +
                mensagem
            );
        }
        return result.data as any;
    }

    async consultarIdVoto(idProcesso): Promise<number> {
        try {
            const result = await fetch<IResultadoGet>(
                UrlsMinuta.consultaIdVoto(idProcesso),
                {
                    method: 'GET',
                    cache: 'no-store',
                }
            );
            if (result && result.status > 300) {
                let mensagem = '';
                if (result.data.identificadorErro) {
                    mensagem = result.data.mensagem;
                }
                throw new Error(
                    'Não foi possível consultar o id do voto.' + mensagem
                );
            }
            return result.data as any;
        } catch (e) {
            if (!e.message.includes('404')) {
                //FIXME: arrumar no frontend para não retornar 404 ao não encontrar o voto.. retornar 200 com mensagem de erro
                console.error(e);
            }
        }
    }

    async converter64toHtml(
        conteudo64,
        retirarDivNFolhas,
        salvarMinuta?: boolean,
        minuta?: Minuta,
        login?: string
    ) {
        const conteudo =
            minuta && minuta.tipoDocumentoMinuta === 'html' ?
                b64EncodeUnicode(MinutaUtils.decodeMinuta(conteudo64))
                :
                btoa(MinutaUtils.decodeMinuta(conteudo64));
        const result = await fetch<IResultadoGet>(
            this.config.urlConversorMinuta64toHtml,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/plain',
                },
                cache: 'no-store',
                body: conteudo,
            }
        );

        if (result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error(
                'Não foi possível converter o conteudo.' + mensagem
            );
        }

        const html = result.data;

        if (salvarMinuta) {
            console.info("conferir se esse ainda tem utilidade"); // TODO

            await this.salvarMinutaHtmlDocx(
                minuta.idProcesso,
                html,
                minuta.tipo,
                login,
                minuta.siglaOrgaoJulgador,
                minuta.publicavel,
                undefined,
                minuta.idProcesso,
                'html'
            );
            await this.salvarMinutaDados(
                minuta.idProcesso,
                html,
                minuta.tipo,
                login,
                minuta.siglaOrgaoJulgador,
                minuta.publicavel,
                undefined,
                minuta.idProcesso,
                'html'
            );
        }

        return html;
    }

    async importarMinuta(file, minuta, login) {
        const fileBase64 = await this.getBase64FromFile(file);
        return this.converter64toHtml(fileBase64, true, true, minuta, login);
    }

    async listarMinutasAssinadas(
        siglaGabinete,
        pagina,
        tamanhoPagina,
        filtros?: {
            numeroProcesso?: string;
            numerosProcesso?: string[];
            tiposMinuta?: Array<string | number>;
            classes?: string[];
            dataInicioAssinatura?: string;
            dataFimAssinatura?: string;
        }
    ): Promise<IMinutaAssinada[] | IMinutaAssinadaPage> {
        const result = await fetch<IMinutaAssinada[] | IMinutaAssinadaPage>(
            UrlsMinuta.minutasAssinadas(siglaGabinete, pagina, tamanhoPagina),
            {
                method: 'POST',
                cache: 'no-store',
                body: filtros || {},
            }
        );
        if (result?.status > 300) {
            throw new Error('Não foi possível consultar as minutas assinadas.');
        }
        return result.data || [];
    }

    async listarMinutasComVotoVencido(siglaGabinete) {
        const result = await fetch<IResultadoGet>(
            UrlsMinuta.listarMinutasComVotoVencido(siglaGabinete),
            {
                method: 'GET',
                cache: 'no-store',
            }
        );
        if (result && result.status > 300) {
            let mensagem = '';
            if (result.data.identificadorErro) {
                mensagem = result.data.mensagem;
            }
            throw new Error(
                'Não foi possível consultar as minutas assinadas.' + mensagem
            );
        }
        const resultado = result.data;
        return resultado;
    }

    async verificaMinuta(numero: number): Promise<string[]> {
        const result = await fetch<string[]>(
            UrlsMinuta.validar(numero.toString()),
            {
                method: 'GET',
                cache: 'no-store',
            }
        );

        return result.data;
    }

    getBase64FromFile(file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise((resolve, reject) => {
            reader.onload = function () {
                if ((reader.result as string).replace) {
                    let encoded = (reader.result as string).replace(
                        /^.*;base64,/,
                        ''
                    );
                    encoded = b64EncodeUnicode(
                        MinutaUtils.decodeMinuta(encoded)
                    );
                    resolve(encoded);
                }
            };
            reader.onerror = function (error) {
                console.error('Error: ', error);
                reject(error);
            };
        });
    }
    async recuperaMinutaParaAssinatura(numeroMinuta: number) {
        const result = await fetch(
            UrlsMinuta.recuperaMinutaAssinatura(numeroMinuta)
        );
        return result.data;
    }

    async recuperaMinutaAssinada(numeroMinuta: number) {
        const result = await fetch(
            UrlsMinuta.recuperaMinutaAssinada(numeroMinuta),
            {
                method: 'GET',
                cache: 'no-store',
            }
        );
        return result.data;
    }

    async visualizarVersaoMinuta(versao: VersaoMinutaDto) {
        const result = await fetch(
            UrlsMinuta.recuperaPreviewVersaoMinuta(versao)
        );
        return result.data;
    }


    async isEmEdicaoExterna(
        numeroMinuta: number[]
    ): Promise<IMinutaEmEdicaoExterna[]> {
        const result = await fetch<IMinutaEmEdicaoExterna[]>(
            UrlsMinuta.minutaEstaEmEdicaoExterna(numeroMinuta)
        );
        return result.data;
    }

    async recuperaCategoriaDasMinutas(numeros: number[]): Promise<any> {
        const result = await fetch<any[]>(Configuracao.urlMinutaGE + 'categorias/consulta', {
            method: 'POST',
            body: numeros
        });
        return result.data;
    }

    async devolverEManterAssinado(numeroDeMinuta: number[]): Promise<any> {
        const result = await fetch<any[]>(Configuracao.urlDevolverMinutasEManterAssinatura.replace("{CPF_DO_USUARIO}", UsuarioStore.login), {
            method: 'POST',
            body: numeroDeMinuta
        });
        return result.data;
    }

    async listarHistoricoAlteracaoSituacaoMinuta(numerosMinutas: number[], apenasUltimaAlteracao: boolean): Promise<HistoricoMinutaDto[]> {
        const result = await fetch<any[]>(UrlsMinuta.historicoAlteracaoSituacaoMinuta(apenasUltimaAlteracao), {
            method: 'POST',
            body: numerosMinutas
        });
        return result.data;
    }

    async listarHistoricoSituacaoMinuta(numeroMinuta: number): Promise<HistoricoSituacaoMinutaDto[]> {
        const result = await fetch<HistoricoSituacaoMinutaDto[]>(
            UrlsMinuta.historicoSituacaoMinuta(numeroMinuta)
        );
        return result.data;
    }

    async listarVinculosPeticaoMinuta(numeroMinuta: any): Promise<any[]> {
        const result = await fetch<any[]>(
            UrlsMinuta.listarVinculosPeticaoMinuta(numeroMinuta)
        );
        return result.data;
    }

    async vincularPeticaoMinuta(numeroMinuta, idPeticao, idUnico) {
        const result = await fetch<any[]>(
            UrlsMinuta.vincularPeticaoMinuta(numeroMinuta, idPeticao, idUnico), {
            method: 'POST',
        });
        return result.data;
    }

    async deletarVinculoPeticaoMinuta(numeroMinuta, idPeticao, idUnico) {
        const result = await fetch<any[]>(
            UrlsMinuta.vincularPeticaoMinuta(numeroMinuta, idPeticao, idUnico), {
            method: 'DELETE',
        });
        return result.data;
    }

    async listarVersoesMinuta(numeroMinuta) {
        const result = await fetch<VersaoMinutaDto[]>(
            UrlsMinuta.listarVersoes(numeroMinuta), {
            method: 'GET',
        });
        return result.data;
    }

    async baixarVersaoMinuta(gedId, tipo, ext): Promise<Blob> {
        const result = await fetch<Blob>(
            UrlsMinuta.baixarVersao(gedId, tipo, ext), {
            method: 'GET',
            responseType: "blob"
        });
        return result.data;
    }

    async restaurarVersaoMinuta(numeroMinuta, gedId, tipo, ext): Promise<Blob> {
        const result = await fetch<Blob>(
            UrlsMinuta.restaurarVersao(numeroMinuta, gedId, tipo, ext), {
            method: 'PUT'
        });
        return result.data;
    }

    async verificaEmentaEDecisao(idsProcesso: number[]): Promise<Map<number, EmentaDecisao>> {
        const result = await fetch<Map<number, EmentaDecisao>>(this.config.urlRecuperarEmentaDecisao, {
            method: 'POST',
            body: idsProcesso
        });

        return result.data;
    }

    async upgradeVoto(numMinuta: number) {
        const result = await fetch(this.config.urlTranformarAgravoEmVoto, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: [numMinuta]
        });
        if (result.status != 200) {
            throw new Error('Erro ao transformar o agravo provido no voto');
        }
    }

    async gerarEmentaAguia(numMinuta: number, estiloEmenta: string) {
        try {
            const result = await fetch(Configuracao.urlGerarEmentaAguia.replace('{NUM_MINUTA}', numMinuta.toString()) + "?estiloEmenta=" + estiloEmenta, {
                method: 'POST', headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
            });
            return result.data
        } catch (error) {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro(error.message + ' - ' + 'Código do erro: ' + error.code)
            }
        }

    }

    async salvarEmentaGeradaAguia(numMinuta: number, ementa) {
        try {
            const result = await fetch(Configuracao.urlSubstituirEmentaAguia.replace('{NUM_MINUTA}', numMinuta.toString()), {
                method: 'POST', headers: { 'Accept': '*/*', 'Content-Type': 'text/plain' },
                body: ementa.trim(),
            });
            return result.data
        } catch (error) {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro(error.message + ' - ' + 'Código do erro: ' + error.code)
            }
        }
    }

    async excluirMinutasAguia(idsMinutas: number[]) {
        const params = new URLSearchParams();
        idsMinutas.forEach(id => params.append('idsMinutas', id.toString()));

        const result = await fetch(
            `${Configuracao.urlExcluirMinutaAguia}?${params.toString()}`,
            {
                method: 'DELETE',
                headers: { 'Accept': '*/*' }
            });

        return result.data
    }

    async restaurarMinutaArquivada(numMinuta: number) {
        await fetch(Configuracao.urlRecuperarMinutaArquivada.replace('{NUM_MINUTA}', numMinuta.toString()), {
            method: 'POST'
        });
    }

}

export default new MinutaApi();
